/**
 * Auto/DUI Quote Wizard — Version H10
 * ======================================================================
 * Component: AutoDUI_QuoteWizard_H10
 *
 * WHAT'S NEW VS H9 (2025-08-20):
 * - Renamed ExampleQuoteWizard_H9 → AutoDUI_QuoteWizard_H10 (requested).
 * - Product mode (DUI vs AUTO) now controlled by URL query param ONLY:
 *     • ?mode=dui (default) or ?mode=auto
 *     • UI mode-switch buttons REMOVED.
 * - VIN UX & validation:
 *     • Live character count shown above input.
 *     • Field turns invalid (red) if length > 0 and != 17 chars.
 * - DEMO_MODE (independent of TEST_MODE):
 *     • When on, form auto-populates with demo data.
 *     • Toggleable in code; also an optional DEMO_MODE_UI checkbox
 *       for QA to switch live (if enabled in code).
 *     • If DEMO_MODE_UI is disabled in code, the checkbox is hidden.
 * - All H9 fields preserved (personal, DUI/Driving History, filings, vehicle, coverage, remarks).
 *
 * HOW TO USE:
 *   <AutoDUI_QuoteWizard_H10 />
 *
 * URL PARAMS:
 *   - mode: "dui" | "auto"   (default "dui")
 *     e.g.  ?mode=auto
 *
 * GLOBAL TOGGLES (edit constants below):
 *   - TEST_MODE:  true => logs payload, skips network
 *   - DEMO_MODE:  true => initial demo data pre-filled
 *   - DEMO_MODE_UI_ENABLED: true => show a UI checkbox to toggle demo mode
 *   - SHOW_VERSION: show H10 version badge/text
 *
 * NOTES:
 *   - Strings-only payload preserved.
 *   - Completion logic unchanged except VIN validation is UX-only (VIN still optional).
 * ======================================================================
In the code that follows: 
--------------------------------------------

Update the DUI Code ExampleQuoteWizard_H9 - be sure to include all the fields from H9.   
Call this version AUTO_DUI_QuoteWizard_H10

TO FIX
1st. The switch between AUTO mode and DUI mode does not appear to be working. 
    Furthermore, make the switch between AUTO mode and DUI mode work via URL and not in the UI. The default should be DUI mode. Finally change the name from ExampleQuoteWizard_H9 to AutoDUI_QuoteWizard_H10 . 
    Be sure to all the documentation for this version in the code.



Items to ADD
---------------
1. Add validation on the VIN number to help users get the correct length. 
Also, show the user the length above the text box in the caption as they enter the vin number into the text box. 

2. Add a DEMO_MODE ( that is independent of the TEST_MODE ) to the code so default data object is auto populated with test data. 

3. ADD a DEMO_MODE_UI that provides a UI checkbox for QA tester to turn DEMO_MODE ( auto populate the default data object ) on and off. 
   
3. If the DEMO_MODE_UI is turned off in the code, the UI should not display the checkbox. 


--------- the code that follows ------------





*/

import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";

/* ============================================================
   DECLARATIONS (global toggles / config + copy)
============================================================ */
const VERSION = "vH10";
const SHOW_VERSION = true;

// Network/test toggles
const TEST_MODE = false; // true ⇒ logs payload, skips network
const API_GATEWAY_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";

// DEMO controls (independent of TEST_MODE)
const DEMO_MODE_DEFAULT = true;              // initial demo state (pre-populate)
const DEMO_MODE_UI_ENABLED = true;           // show/hide the checkbox for QA
const DEMO_CHECKBOX_LABEL = "Demo Mode (auto-fill sample data)";

type ProductMode = "dui" | "auto";
// URL-driven default
const DEFAULT_MODE: ProductMode = "dui";

// Explanations visibility on vertical layout (false = show all; true = only active)
const EXPLAIN_HIDE_INACTIVE = false;

// Progress cap helper (0..100 percentage in, clamp to 0..99 pre-submit)
const clampPct = (p: number) => Math.min(99, Math.max(0, Math.round(p)));

/* ---------- Section titles & descriptions (by mode) ---------- */
const sectionTitlePersonalInfo = "Personal Information";
const sectionTitleDuiInfo = "DUI Info";
const sectionTitleDrivingHistory = "Driving History";
const sectionTitleFilings = "SR-22 / FR-44 Requirements";
const sectionTitleVehicle = "Vehicle";
const sectionTitleCoverage = "Coverage (Optional)";
const sectionTitleRemarks = "Remarks (Optional)";

const descriptionPersonalInfo =
  "Provide your legal name, DOB, contact info, and mailing address so we can match records and contact you with your quote.";

const descriptionDuiInfo =
  "Enter your DUI history (count and most recent date) and license info. This affects eligibility and rates.";

const descriptionDrivingHistory =
  "Tell us about your recent driving history (moving violations, at-fault accidents). This helps determine your rate.";

const descriptionFilings =
  "Some states require an SR-22 certificate; in Virginia or Florida you may need an FR-44. Select filing type, state, and duration.";

const descriptionVehicle =
  "If you own a car, provide year/make/model, optional VIN, mileage and use. If not, you can skip this section.";

const descriptionCoverage =
  "Choose a coverage preference now or leave blank to discuss with an agent. This is optional.";

const descriptionRemarks =
  "Add any notes or preferences for your agent. This does not affect completion.";

/* ============================================================
   Types
============================================================ */
type ViewMode = "wizard" | "vertical";
export type FormShape = Record<string, unknown>;
export type SetForm = (patch: FormShape) => void;

export interface WizardSection {
  id: string;
  title: string;
  explanation: string;
  enabled?: boolean;
  render: (form: FormShape, setForm: SetForm) => React.ReactNode;
}

/* ============================================================
   Helpers
============================================================ */
const S = (v: unknown): string | null => {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return String(v);
};
const hasVal = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
};
const readQueryMode = (): ProductMode => {
  try {
    const qs = new URLSearchParams(window.location.search);
    const m = (qs.get("mode") || "").toLowerCase();
    if (m === "auto" || m === "dui") return m as ProductMode;
  } catch { /* no-op for SSR */ }
  return DEFAULT_MODE;
};

/* ============================================================
   Data
============================================================ */
const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const FR44_STATES = [
  { value: "VA", label: "Virginia" },
  { value: "FL", label: "Florida" },
];

/* ============================================================
   Completion Schema (per-mode)
============================================================ */
type SectionKeys = {
  id: string;
  required: (form: FormShape, mode: ProductMode) => string[];
  optional?: (form: FormShape, mode: ProductMode) => string[];
};

const completionSchema: SectionKeys[] = [
  {
    id: "personal-info",
    required: () => [
      "firstName", "lastName", "dob", "email", "phone",
      "address1", "city", "state", "zip",
    ],
  },
  {
    id: "driver-history",
    required: (form, mode) => {
      if (mode === "dui") {
        return ["duiCount", "duiDate", "licenseNumber", "issuingState"];
      } else {
        return ["movingViolations", "atFaultAccidents", "licenseNumber", "issuingState"];
      }
    },
  },
  {
    id: "filings",
    required: (form, mode) => {
      if (mode === "dui") return ["filingType", "filingState", "filingDuration"];
      const need = (form.needFiling as string) === "yes";
      return need ? ["filingType", "filingState", "filingDuration"] : [];
    },
  },
  {
    id: "vehicle",
    required: (form) => {
      const ownCar = ((form.ownCar as string) ?? "yes") === "yes";
      if (!hasVal(form.ownCar)) return ["ownCar"];
      if (!ownCar) return ["ownCar"];
      return ["ownCar", "carYear", "carMake", "carModel", "carAnnualMileage", "carUse"]; // VIN optional
    },
  },
  {
    id: "coverage",
    required: () => [],
    optional: () => ["coverageType", "compDeductible"],
  },
  {
    id: "contact-remarks",
    required: () => [],
    optional: () => ["remarks"],
  },
];

const requiredKeysForSection = (sectionId: string, form: FormShape, mode: ProductMode): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.required(form, mode) ?? [];

const optionalKeysForSection = (sectionId: string, form: FormShape, mode: ProductMode): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.optional?.(form, mode) ?? [];

const sectionIsComplete = (sectionId: string, form: FormShape, mode: ProductMode): boolean => {
  const req = requiredKeysForSection(sectionId, form, mode);
  if (req.length > 0) return req.every((k) => hasVal(form[k]));
  const opt = optionalKeysForSection(sectionId, form, mode);
  return opt.length > 0 && opt.some((k) => hasVal(form[k]));
};

const overallCompletion = (enabledSectionIds: string[], form: FormShape, mode: ProductMode): number => {
  const allReq: string[] = enabledSectionIds.flatMap((id) => requiredKeysForSection(id, form, mode));
  const uniq = Array.from(new Set(allReq));
  if (uniq.length === 0) return 0;
  const filled = uniq.reduce((acc, k) => (hasVal(form[k]) ? acc + 1 : acc), 0);
  return (filled / uniq.length) * 100;
};

/* ============================================================
   Controls (prev/next/submit)
============================================================ */
const WizardControls: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
}> = ({ onPrev, onNext, onSubmit, isFirst, isLast, submitting }) => (
  <div className="d-flex justify-content-between">
    <Button variant="secondary" onClick={onPrev} disabled={isFirst || !!submitting}>
      Previous
    </Button>
    {isLast ? (
      <Button variant="success" onClick={onSubmit} disabled={!!submitting}>
        {submitting ? "Processing..." : "Submit"}
      </Button>
    ) : (
      <Button variant="primary" onClick={onNext} disabled={!!submitting}>
        Next
      </Button>
    )}
  </div>
);

/* ============================================================
   Dual View Wizard (unchanged)
============================================================ */
export const DualViewWizard_H10: React.FC<{
  mode?: ViewMode;
  productMode: ProductMode;
  sections: WizardSection[];
  initialForm?: FormShape;
  className?: string;
  onSubmit?: (form: FormShape) => void;
  submitting?: boolean;
  submitted?: boolean;
}> = ({
  mode = "wizard",
  productMode,
  sections,
  initialForm = {},
  className,
  onSubmit,
  submitting = false,
  submitted = false,
}) => {
  const enabledSections = useMemo(
    () => sections.filter((s) => s.enabled !== false),
    [sections]
  );

  const [form, setFormState] = useState<FormShape>(initialForm);
  const setForm: SetForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const [current, setCurrent] = useState(0);
  const [activeId, setActiveId] = useState<string>(enabledSections[0]?.id ?? "");

  const ids = useMemo(() => enabledSections.map((s) => s.id), [enabledSections]);
  const activeIndex = Math.max(0, ids.indexOf(activeId));
  const total = Math.max(1, enabledSections.length);
  const enabledIds = enabledSections.map((s) => s.id);

  const rawPct = overallCompletion(enabledIds, form, productMode);
  const pctLabel = submitted ? 100 : clampPct(rawPct);
  const pct = submitted ? 100 : clampPct(rawPct);

  if (mode === "vertical") {
    return (
      <div className={className}>
        <Card className="position-sticky mb-3" style={{ top: 16, zIndex: 1 }}>
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  {productMode === "dui" ? "DUI Quote" : "Auto Quote"} {SHOW_VERSION ? `— ${VERSION}` : ""} · Section {activeIndex + 1} of {total}
                </strong>{" "}
                — {enabledSections[activeIndex]?.title}
              </div>
              <div style={{ minWidth: 180 }}>
                <ProgressBar now={pct} label={`${pctLabel}%`} />
              </div>
            </div>
          </Card.Header>
        </Card>

        {enabledSections.map((s) => {
          const isActive = activeId === s.id;
          const showExplain = isActive || !EXPLAIN_HIDE_INACTIVE;
          const isComplete = sectionIsComplete(s.id, form, productMode);

          const cardClasses = [
            "border",
            isComplete ? "border-success bg-success-subtle" : "",
            isActive && !isComplete ? "border-primary" : "",
          ].join(" ").trim();

          const headerClasses = isActive ? "bg-primary text-white" : "";

          return (
            <Row key={s.id} className="mb-3" style={{ scrollMarginTop: 90 }}>
              <Col
                md={8}
                onClick={() => setActiveId(s.id)}
                onFocusCapture={() => setActiveId(s.id)}
              >
                <Card className={cardClasses}>
                  <Card.Header className={headerClasses}>
                    {s.title}
                    {isComplete && !isActive && <Badge bg="success" className="ms-2">Complete</Badge>}
                    {isComplete && isActive && <Badge bg="light" text="dark" className="ms-2">Complete</Badge>}
                  </Card.Header>
                  <Card.Body>{s.render(form, setForm)}</Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                {showExplain && (
                  <Card className={isActive ? "border border-primary" : "border"}>
                    <Card.Header className={isActive ? "bg-primary text-white" : ""}>
                      Section Help
                    </Card.Header>
                    <Card.Body>
                      <strong>{s.title}</strong>
                      <p className="mb-0">{s.explanation}</p>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          );
        })}

        <div className="d-flex justify-content-end mt-3">
          <Button variant="success" onClick={() => onSubmit?.(form)} disabled={!!submitting}>
            {submitting ? "Processing..." : "Submit"}
          </Button>
        </div>
      </div>
    );
  }

  const step = enabledSections[current];
  const stepComplete = sectionIsComplete(step.id, form, productMode);
  const cardClasses = stepComplete ? "border border-success bg-success-subtle" : "";

  return (
    <Card className={`${className ?? ""} ${cardClasses}`}>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>
              {productMode === "dui" ? "DUI Quote" : "Auto Quote"} {SHOW_VERSION ? `— ${VERSION}` : ""} · Step {current + 1} of {total}
            </strong>{" "}
            — {step.title}
          </div>
          <div style={{ minWidth: 160 }}>
            <ProgressBar now={pct} label={`${pctLabel}%`} />
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {step.render(form, setForm)}

        <Card className="mt-3">
          <Card.Header>Section Help</Card.Header>
          <Card.Body>
            <strong>{step.title}</strong>
            <p className="mb-0">{step.explanation}</p>
          </Card.Body>
        </Card>

        <hr />
        <WizardControls
          onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
          onNext={() => setCurrent((c) => Math.min(total - 1, c + 1))}
          onSubmit={() => onSubmit?.(form)}
          isFirst={current === 0}
          isLast={current === total - 1}
          submitting={submitting}
        />
      </Card.Body>
    </Card>
  );
};

/* ============================================================
   Example Usage — Version H10 (URL-driven product mode)
============================================================ */
export const AutoDUI_QuoteWizard_H10: React.FC = () => {
  // View mode control (kept from H9)
  const [uiMode, setUiMode] = useState<ViewMode>("vertical");

  // Product mode now from URL (default DUI)
  const [form_MODE, setProductMode] = useState<ProductMode>(readQueryMode());

  // DEMO mode (separate from TEST_MODE)
  const [demoMode, setDemoMode] = useState<boolean>(DEMO_MODE_DEFAULT);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Watch for URL mode changes (e.g., client-side nav)
  useEffect(() => {
    const handlePop = () => setProductMode(readQueryMode());
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  // Base (empty) initial state
  const initialBase: FormShape = { ownCar: "yes", needFiling: "no" };

  // Demo data set (kept realistic; VIN optional)
  const DEMO_DATA: FormShape = {
    ...initialBase,
    // Personal
    firstName: "Alex",
    lastName: "Driver",
    dob: "1991-07-15",
    email: "alex.driver@example.com",
    phone: "(303) 555-0180",
    address1: "123 Main St",
    address2: "Apt 4B",
    city: "Denver",
    state: "CO",
    zip: "80202",
    // Driver/DUI / Driving history
    licenseNumber: "A123456789",
    issuingState: "CO",
    duiCount: "1",
    duiDate: "2023-10-05",
    movingViolations: "1 speeding ticket",
    atFaultAccidents: "0",
    needFiling: "yes",
    // Filings
    filingType: "sr22",
    filingState: "CO",
    filingDuration: "3",
    // Vehicle
    carYear: "2018",
    carMake: "Toyota",
    carModel: "Camry",
    carUse: "commute",
    carAnnualMileage: "12000",
    carVIN: "1HGCM82633A004352", // 17 chars
    // Coverage/Remarks
    coverageType: "liability",
    compDeductible: "500",
    remarks: "Evening call preferred.",
  };

  // Build sections based on product mode
  const sections: WizardSection[] = useMemo(() => {
    const personal: WizardSection = {
      id: "personal-info",
      title: sectionTitlePersonalInfo,
      explanation: descriptionPersonalInfo,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="firstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.firstName as string) ?? ""}
                  onChange={(e) => setForm({ firstName: e.target.value })}
                  placeholder="Jane"
                  isValid={hasVal(form.firstName)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="lastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.lastName as string) ?? ""}
                  onChange={(e) => setForm({ lastName: e.target.value })}
                  placeholder="Doe"
                  isValid={hasVal(form.lastName)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="dob">
                <Form.Label>Date of Birth</Form.Label>
                <Form.Control
                  type="date"
                  value={(form.dob as string) ?? ""}
                  onChange={(e) => setForm({ dob: e.target.value })}
                  isValid={hasVal(form.dob)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={(form.email as string) ?? ""}
                  onChange={(e) => setForm({ email: e.target.value })}
                  placeholder="you@example.com"
                  isValid={hasVal(form.email)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="phone">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.phone as string) ?? ""}
                  onChange={(e) => setForm({ phone: e.target.value })}
                  placeholder="(555) 555-1234"
                  inputMode="tel"
                  isValid={hasVal(form.phone)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="address1Input">
                <Form.Label>Address Line 1</Form.Label>
                <Form.Control
                //   id="address1Input"
                  type="text"
                  value={(form.address1 as string) ?? ""}
                  onChange={(e) => setForm({ address1: e.target.value })}
                  placeholder="123 Main St"
                  autoComplete="address-line1"
                  isValid={hasVal(form.address1)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="address2Input">
                <Form.Label>Address Line 2 (optional)</Form.Label>
                <Form.Control
                //   id="address2Input"
                  type="text"
                  value={(form.address2 as string) ?? ""}
                  onChange={(e) => setForm({ address2: e.target.value })}
                  placeholder="Apt / Suite / Unit"
                  autoComplete="address-line2"
                  isValid={hasVal(form.address2)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="city">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.city as string) ?? ""}
                  onChange={(e) => setForm({ city: e.target.value })}
                  isValid={hasVal(form.city)}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group controlId="state">
                <Form.Label>State</Form.Label>
                <Form.Select
                  value={(form.state as string) ?? ""}
                  onChange={(e) => setForm({ state: e.target.value })}
                  isValid={hasVal(form.state)}
                >
                  <option value="">Select a state…</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group controlId="zip">
                <Form.Label>ZIP Code</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.zip as string) ?? ""}
                  onChange={(e) => setForm({ zip: e.target.value })}
                  placeholder="80202"
                  inputMode="numeric"
                  isValid={hasVal(form.zip)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const driverHistory: WizardSection =
      form_MODE === "dui"
        ? {
            id: "driver-history",
            title: sectionTitleDuiInfo,
            explanation: descriptionDuiInfo,
            enabled: true,
            render: (form, setForm) => (
              <Form>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group controlId="duiCount">
                      <Form.Label># of DUI/DWI Convictions</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.duiCount as string) ?? ""}
                        onChange={(e) => setForm({ duiCount: e.target.value })}
                        placeholder="e.g. 1"
                        inputMode="numeric"
                        isValid={hasVal(form.duiCount)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="duiDate">
                      <Form.Label>Most Recent DUI Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={(form.duiDate as string) ?? ""}
                        onChange={(e) => setForm({ duiDate: e.target.value })}
                        isValid={hasVal(form.duiDate)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="licenseNumber">
                      <Form.Label>License Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.licenseNumber as string) ?? ""}
                        onChange={(e) => setForm({ licenseNumber: e.target.value })}
                        placeholder="A123-456-789"
                        isValid={hasVal(form.licenseNumber)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="issuingState">
                      <Form.Label>Issuing State</Form.Label>
                      <Form.Select
                        value={(form.issuingState as string) ?? ""}
                        onChange={(e) => setForm({ issuingState: e.target.value })}
                        isValid={hasVal(form.issuingState)}
                      >
                        <option value="">Select a state…</option>
                        {US_STATES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            ),
          }
        : {
            id: "driver-history",
            title: sectionTitleDrivingHistory,
            explanation: descriptionDrivingHistory,
            enabled: true,
            render: (form, setForm) => (
              <Form>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group controlId="movingViolations">
                      <Form.Label>Moving Violations (last 3–5 yrs)</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.movingViolations as string) ?? ""}
                        onChange={(e) => setForm({ movingViolations: e.target.value })}
                        placeholder="e.g. 1 speeding ticket"
                        isValid={hasVal(form.movingViolations)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="atFaultAccidents">
                      <Form.Label>At-Fault Accidents (last 3–5 yrs)</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.atFaultAccidents as string) ?? ""}
                        onChange={(e) => setForm({ atFaultAccidents: e.target.value })}
                        placeholder="e.g. 0"
                        isValid={hasVal(form.atFaultAccidents)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="licenseNumber">
                      <Form.Label>License Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.licenseNumber as string) ?? ""}
                        onChange={(e) => setForm({ licenseNumber: e.target.value })}
                        placeholder="A123-456-789"
                        isValid={hasVal(form.licenseNumber)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="issuingState">
                      <Form.Label>Issuing State</Form.Label>
                      <Form.Select
                        value={(form.issuingState as string) ?? ""}
                        onChange={(e) => setForm({ issuingState: e.target.value })}
                        isValid={hasVal(form.issuingState)}
                      >
                        <option value="">Select a state…</option>
                        {US_STATES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* AUTO mode only: toggle to unlock SR-22 section */}
                  <Col md={12}>
                    <Form.Check
                      type="checkbox"
                      id="needFiling"
                      label="I need an SR-22/FR-44 filing"
                      checked={((form.needFiling as string) ?? "no") === "yes"}
                      onChange={(e) => setForm({ needFiling: e.target.checked ? "yes" : "no" })}
                    />
                  </Col>
                </Row>
              </Form>
            ),
          };

    const filings: WizardSection = {
      id: "filings",
      title: sectionTitleFilings,
      explanation: descriptionFilings,
      enabled: form_MODE === "dui" || ((form: FormShape) => (form.needFiling as string) === "yes") as any,
      render: (form, setForm) => {
        const filingType = (form.filingType as string) ?? "";
        const isFR44 = filingType === "fr44";
        const filingStates = isFR44 ? FR44_STATES : US_STATES;

        return (
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="filingType">
                  <Form.Label>Filing Type</Form.Label>
                  <Form.Select
                    value={filingType}
                    onChange={(e) => setForm({ filingType: e.target.value, filingState: "" })}
                    isValid={hasVal(filingType)}
                  >
                    <option value="">Select...</option>
                    <option value="sr22">SR-22</option>
                    <option value="fr44">FR-44</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="filingState">
                  <Form.Label>Filing State</Form.Label>
                  <Form.Select
                    value={(form.filingState as string) ?? ""}
                    onChange={(e) => setForm({ filingState: e.target.value })}
                    isValid={hasVal(form.filingState)}
                  >
                    <option value="">Select a state…</option>
                    {filingStates.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Form.Select>
                  {isFR44 && (
                    <div className="mt-1">
                      <Badge bg="info">Note</Badge>{" "}
                      FR-44 filings are available in <strong>Virginia (VA)</strong> and <strong>Florida (FL)</strong> only.
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="filingDuration">
                  <Form.Label>Expected Filing Duration (years)</Form.Label>
                  <Form.Control
                    type="text"
                    value={(form.filingDuration as string) ?? ""}
                    onChange={(e) => setForm({ filingDuration: e.target.value })}
                    placeholder="e.g. 3"
                    inputMode="numeric"
                    isValid={hasVal(form.filingDuration)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        );
      },
    };

    const vehicle: WizardSection = {
      id: "vehicle",
      title: sectionTitleVehicle,
      explanation: descriptionVehicle,
      enabled: true,
      render: (form, setForm) => {
        const ownCar = ((form.ownCar as string) ?? "yes") === "yes";
        const vin = ((form.carVIN as string) ?? "").trim();
        const vinLen = vin.length;
        const vinInvalid = vinLen > 0 && vinLen !== 17; // UX validation only

        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="ownCar"
                  label="I own a car"
                  checked={ownCar}
                  onChange={(e) => setForm({ ownCar: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {ownCar && (
                <>
                  <Col md={4}>
                    <Form.Group controlId="carYear">
                      <Form.Label>Car Year</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.carYear as string) ?? ""}
                        onChange={(e) => setForm({ carYear: e.target.value })}
                        placeholder="e.g. 2018"
                        inputMode="numeric"
                        isValid={hasVal(form.carYear)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="carMake">
                      <Form.Label>Car Make</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.carMake as string) ?? ""}
                        onChange={(e) => setForm({ carMake: e.target.value })}
                        isValid={hasVal(form.carMake)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="carModel">
                      <Form.Label>Car Model</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.carModel as string) ?? ""}
                        onChange={(e) => setForm({ carModel: e.target.value })}
                        isValid={hasVal(form.carModel)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group controlId="carVIN">
                      <div className="d-flex justify-content-between align-items-end">
                        <Form.Label className="mb-0">Car VIN (optional)</Form.Label>
                        <small className={vinInvalid ? "text-danger" : "text-muted"}>
                          VIN length: {vinLen}/17 {vinInvalid ? "— should be exactly 17 characters" : ""}
                        </small>
                      </div>
                      <Form.Control
                        type="text"
                        value={(form.carVIN as string) ?? ""}
                        onChange={(e) => setForm({ carVIN: e.target.value.toUpperCase() })}
                        placeholder="Enter 17 characters (optional)"
                        isInvalid={vinInvalid}
                        isValid={!vinInvalid && vinLen > 0}
                      />
                      {vinInvalid && (
                        <Form.Control.Feedback type="invalid">
                          VINs are exactly 17 characters. Double-check and try again.
                        </Form.Control.Feedback>
                      )}
                      <Form.Text muted>
                        Tip: VINs exclude I, O, and Q. You can usually find your VIN on your registration or insurance card.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="carAnnualMileage">
                      <Form.Label>Annual Mileage</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.carAnnualMileage as string) ?? ""}
                        onChange={(e) => setForm({ carAnnualMileage: e.target.value })}
                        placeholder="e.g. 12000"
                        inputMode="numeric"
                        isValid={hasVal(form.carAnnualMileage)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="carUse">
                      <Form.Label>Primary Use</Form.Label>
                      <Form.Select
                        value={(form.carUse as string) ?? ""}
                        onChange={(e) => setForm({ carUse: e.target.value })}
                        isValid={hasVal(form.carUse)}
                      >
                        <option value="">Select...</option>
                        <option value="commute">Commute</option>
                        <option value="personal">Personal</option>
                        <option value="business">Business</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    const coverage: WizardSection = {
      id: "coverage",
      title: sectionTitleCoverage,
      explanation: descriptionCoverage,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="coverageType">
                <Form.Label>Coverage Type</Form.Label>
                <Form.Select
                  value={(form.coverageType as string) ?? ""}
                  onChange={(e) => setForm({ coverageType: e.target.value })}
                  isValid={hasVal(form.coverageType)}
                >
                  <option value="">Select...</option>
                  <option value="liability">Liability Only</option>
                  <option value="full">Full Coverage</option>
                  <option value="nonOwner">Non-Owner</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="compDeductible">
                <Form.Label>Comp Deductible</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.compDeductible as string) ?? ""}
                  onChange={(e) => setForm({ compDeductible: e.target.value })}
                  placeholder="e.g. 500"
                  inputMode="numeric"
                  isValid={hasVal(form.compDeductible)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const remarks: WizardSection = {
      id: "contact-remarks",
      title: sectionTitleRemarks,
      explanation: descriptionRemarks,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group controlId="remarks">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={(form.remarks as string) ?? ""}
                  onChange={(e) => setForm({ remarks: e.target.value })}
                  placeholder="Anything else we should know?"
                  isValid={hasVal(form.remarks)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const needFiling = (form: FormShape) =>
      form_MODE === "dui" || ((form.needFiling as string) ?? "no") === "yes";

    const filingsWrapped: WizardSection = {
      ...filings,
      enabled: form_MODE === "dui" ? true : undefined,
      render: filings.render,
    };

    return [personal, driverHistory, filingsWrapped, vehicle, coverage, remarks];
  }, [form_MODE]);

  // Build initial form object (demo vs blank)
  const initialForm = demoMode ? DEMO_DATA : initialBase;

  // Submission handler (unchanged from H9; preserves strings-only fields)
  const handleSubmit = async (payload: FormShape) => {
    setSubmitError(null);
    setSubmitting(true);

    const enabledIds = sections.filter(s => s.enabled !== false).map(s => s.id);
    const raw = overallCompletion(enabledIds, payload, form_MODE);
    const pctToSend = clampPct(raw);

    const customer_DATA = {
      // Meta
      productMode: form_MODE,
      submittedAtIso: new Date().toISOString(),
      source: `Quote Wizard — ${VERSION}`,

      // Personal
      firstName: S(payload.firstName),
      lastName: S(payload.lastName),
      dob: S(payload.dob),
      email: S(payload.email),
      phone: S(payload.phone),
      address1: S(payload.address1),
      address2: S(payload.address2),
      city: S(payload.city),
      state: S(payload.state),
      zip: S(payload.zip),

      // Driver/DUI or Driving History
      licenseNumber: S(payload.licenseNumber),
      issuingState: S(payload.issuingState),
      duiCount: S(payload.duiCount),
      duiDate: S(payload.duiDate),
      needFiling: S(payload.needFiling),

      movingViolations: S(payload.movingViolations),
      atFaultAccidents: S(payload.atFaultAccidents),

      // Filings
      filingType: S(payload.filingType),
      filingState: S(payload.filingState),
      filingDuration: S(payload.filingDuration),

      // Vehicle
      ownCar: S(payload.ownCar),
      carYear: S(payload.carYear),
      carMake: S(payload.carMake),
      carModel: S(payload.carModel),
      carUse: S(payload.carUse),
      carAnnualMileage: S(payload.carAnnualMileage),
      carVIN: S(payload.carVIN),

      // Coverage (optional)
      coverageType: S(payload.coverageType),
      compDeductible: S(payload.compDeductible),

      // Remarks (optional)
      remarks: S(payload.remarks),

      // Progress metric (STRING pre-submit)
      formPercentComplete: String(pctToSend),

      domainName: String(window.location.hostname),
      itemCategory: String("Customer.AUTO.quote"),
      itemClass: String("quote.request.formMode." + form_MODE),
      itemType: String("legacy.auto"),//HARD CODED - THIS HITS THE SWITCH STATEMENT IN THE LAMBDA FUCTION FOR THE MANUAL DATA MARSHALING 
      clientVersion: String(VERSION + ".AUTO.DUI"),
    };

    if (TEST_MODE) {
      console.log("TEST MODE — customer_DATA:", customer_DATA);
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer_DATA),
      });
      if (!resp.ok) throw new Error("network");
      setSubmitted(true);
    } catch {
      setSubmitError("Error: network error, try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container my-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Header className="fw-bold">Thank you!</Card.Header>
              <Card.Body>
                <p className="mb-0">
                  An insurance agent will reach out to you to review and complete your application.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container my-4">
      {/* Page Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h3 className="m-0">
          {form_MODE === "dui" ? "DUI Quote" : "Auto Quote"} {SHOW_VERSION ? `— ${VERSION}` : ""}
        </h3>

        <div className="d-flex align-items-center gap-3">
          {/* DEMO MODE UI (optional) */}
          {DEMO_MODE_UI_ENABLED && (
            <Form.Check
              type="checkbox"
              id="demoModeToggle"
              label={DEMO_CHECKBOX_LABEL}
              checked={demoMode}
              onChange={(e) => setDemoMode(e.target.checked)}
              disabled={submitting}
            />
          )}

          {/* Keep only View switches (mode via URL now) */}
          <div className="d-flex gap-2">
            <Button
              variant={uiMode === "wizard" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("wizard")}
              disabled={submitting}
            >
              Wizard View
            </Button>
            <Button
              variant={uiMode === "vertical" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("vertical")}
              disabled={submitting}
            >
              Vertical View
            </Button>
          </div>
        </div>
      </div>

      {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

      {/* Re-mount the wizard when demoMode toggles to re-seed initial values */}
      <DualViewWizard_H10
        key={demoMode ? "demo-on" : "demo-off"}
        mode={uiMode}
        productMode={form_MODE}
        sections={sections}
        initialForm={demoMode ? DEMO_DATA : initialBase}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitted={false}
      />
    </div>
  );
};
